import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import WebSocketService from "../services/websocket";
import {
  getSelectedCompany,
  getSelectedPerson,
  getUserDetails,
  getUserToken,
} from "../utils/auth";
import { getAdminId } from "../utils/authId";
import { toast } from "react-toastify";

const useChatHome = (refreshChat) => {
  const [text, setText] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const inputRef = useRef(null);
  const [sendingmessage, setSendingMessage] = useState([]);
  const userData = getUserDetails() || {};
  const selectedPerson = getSelectedPerson();
  const selectedComp = getSelectedCompany();
  const adminId = getAdminId();
  const [recivingMessage, setRecivingMessage] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [typing, setTyping] = useState("");
  const [mentionInfo, setMentionInfo] = useState([]);
  const menstion_id = [...new Set(mentionInfo?.map((id) => id.id))];
  // const [isSending, setIsSending] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [pendingMessages, setPendingMessages] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [deletedMessages, setDeletedMessages] = useState([]);
  const [pinUpdates, setPinUpdates] = useState([]);

  const selectedUser = useMemo(() => {
    return selectedPerson || {};
  }, [selectedPerson]);

  const debouncedTyping = useCallback(() => {
    if (typingTimeout) clearTimeout(typingTimeout);

    const newTimeout = setTimeout(() => {
      WebSocketService.typingUser(selectedUser?.chat_unique);
    }, 100);

    setTypingTimeout(newTimeout);
  }, [selectedUser?.chat_unique, typingTimeout]);

  useEffect(() => {
    const token = getUserToken();
    WebSocketService.connect(
      token,
      selectedUser?.chat_unique,
      selectedUser?.is_group_chat
    );
    WebSocketService.markMessageAsRead(
      selectedUser?.is_group_chat,
      selectedUser?.chat_unique
    );

    const messageHandler = (data) => {
      const now = new Date();
      if (data.message.message_type === "sent" && data.message.status === 200) {
        setPendingMessages(false);
        setTyping("");
        const newMessage = {
          message: data.message.content,
          file_url: data.message.file_url,
          time: data?.message.time,
          date: now.toISOString().split("T")[0],
          emp_pic: userData.emp_pic,
          message_id: data.message.message_id,
          user_id: data?.message.user_id,
          is_forward: data?.message.is_forward,
        };
        setSendingMessage((prev) => [...prev, newMessage]);
      }
      if (data.receiver !== selectedUser?.chat_unique) return;

      if (data.message.message_type === "received") {
        if (data.message.reaction === "reaction") {
          setReactions({
            reaction: data.message.reactions[0],
            action: data.message.emoji_add_or_remove,
            sender_id: data.sender,
          });
          return;
        }

        if (data.type === "delete") {
          const messageId = data.message_id;
          const chatUniqueId = data.chat_unique_id;
          setDeletedMessages((prev) => [...prev, { messageId, chatUniqueId }]);
          return;
        }
        if (data.type === "pinned" || data.type === "unpin") {
          const pinData = {
            message_id: data.message_id,
            chat_unique_id: data.chat_unique_id,
            is_pinned: data.is_pinned,
          };

          setPinUpdates((prev) => {
            const filtered = prev.filter(
              (pin) =>
                !(
                  pin.message_id === pinData.message_id &&
                  pin.chat_unique_id === pinData.chat_unique_id
                )
            );
            return [...filtered, pinData];
          });
          return;
        }
        if (data.is_typing === "typing") {
          setTyping("typing");
          setTimeout(() => setTyping(null), 1000);
          return;
        }

        WebSocketService.markMessageAsRead(
          selectedUser.is_group_chat,
          selectedUser.chat_unique
        );

        const latestMessage = {
          name: data.message.name,
          message: data.message.content,
          file_url: data.message.file_url,
          time: data?.message.time,
          date: now.toISOString().split("T")[0],
          emp_pic: data?.message?.emp_pic,
          message_id: data.message.message_id,
          user_id: data?.message.user_id,
          is_forward: data?.message.is_forward,
        };

        setRecivingMessage((prev) => {
          if (
            prev.some(
              (msg) =>
                msg.message === latestMessage.message &&
                msg.time === latestMessage.time
            )
          ) {
            return prev;
          }
          return [...prev, latestMessage];
        });
        setTyping("");
      }
    };

    const errorHandler = () => {
      // toast.error("Unable to send message. Please try again.", {
      //   position: "top-center",
      //   autoClose: 3000,
      //   closeButton: false,
      //   className: "custom_toast",
      // });
    };

    WebSocketService.registerCallbacks({
      onmessage: messageHandler,
      onerror: errorHandler,
    });

    return () => {
      WebSocketService.registerCallbacks({
        onmessage: null,
        onerror: null,
      });
    };
  }, [
    selectedUser?.chat_unique,
    refreshChat,
    selectedUser?.register_id,
    selectedUser?.is_group_chat,
    userData.emp_pic,
  ]);

  const replaceMentionsWithTags = (deltaOps) => {
    if (!Array.isArray(deltaOps)) {
      console.error("deltaOps is not an array:", deltaOps);
      return [];
    }

    return deltaOps.reduce((acc, op) => {
      let currentOp = { ...op };

      if (
        currentOp.insert &&
        typeof currentOp.insert === "object" &&
        currentOp.insert.image &&
        currentOp.insert.image.includes(
          "cdn.jsdelivr.net/npm/emoji-datasource-apple"
        )
      ) {
        const hexParts = currentOp.insert.image
          .match(/\/([^/]+)\.png$/)?.[1]
          .split("-");

        if (hexParts) {
          try {
            const codePoints = hexParts.map((hex) => parseInt(hex, 16));
            if (!codePoints.some(isNaN)) {
              const emoji = String.fromCodePoint(...codePoints);
              currentOp = { insert: emoji };
            }
          } catch {}
        }
      }

      if (currentOp.insert && typeof currentOp.insert === "string") {
        let text = currentOp.insert;
        let lastIndex = 0;
        const mentionRegex = /@([^\s]+)/g;
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
          const mentionText = match[0];
          const username = match[1];

          const mentionedUser = mentionInfo.find(
            (user) => user.first_name === username
          );

          if (match.index > lastIndex) {
            acc.push({
              insert: text.substring(lastIndex, match.index),
              attributes: currentOp.attributes || {},
            });
          }

          if (mentionedUser) {
            acc.push({
              insert: `<@${mentionedUser.id}>`,
              attributes: { ...(currentOp.attributes || {}), mention: true },
            });
          } else {
            acc.push({
              insert: mentionText,
              attributes: currentOp.attributes || {},
            });
          }

          lastIndex = match.index + mentionText.length;
        }

        if (lastIndex < text.length) {
          acc.push({
            insert: text.substring(lastIndex),
            attributes: currentOp.attributes || {},
          });
        }

        return acc;
      }

      acc.push(currentOp);
      return acc;
    }, []);
  };

  const getFileExtension = (fileName) => {
    return fileName.split(".").pop().toLowerCase();
  };

  const convertFileToBase64 = async (file) => {
    try {
      const reader = new FileReader();
      const promise = new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64Data = reader.result.split(",")[1];
          resolve({
            file_name: file.name,
            file_data: base64Data,
            file_type: file.type,
            file_extension: getFileExtension(file.name),
          });
        };
        reader.onerror = (error) => reject(error);
      });
      reader.readAsDataURL(file);
      return await promise;
    } catch (error) {
      throw error;
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const remainingSlots = 5 - uploadedFiles.length;
    const maxTotalSize = 10 * 1024 * 1024;

    const allSelectedFiles = [...uploadedFiles.map((f) => f.file), ...files];
    const totalSize = allSelectedFiles.reduce((sum, f) => sum + f.size, 0);

    if (totalSize > maxTotalSize) {
      toast.error("Total upload limit is 10 MB .", {
        position: "top-center",
        autoClose: 2000,
        closeButton: false,
        className: "custom_toast",
      });
      return;
    }

    // Check for max 5 files
    if (files.length > remainingSlots) {
      toast.error(
        `You can only upload ${remainingSlots} more file${
          remainingSlots > 1 ? "s" : ""
        }.`,
        {
          position: "top-center",
          autoClose: 2000,
          closeButton: false,
          className: "custom_toast",
        }
      );
      return;
    }

    const limitedFiles = files.slice(0, remainingSlots);
    const newFiles = limitedFiles.map((file) => {
      const previewUrl = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null;

      return {
        id: crypto.randomUUID(),
        file,
        name: file.name,
        type: file.type,
        url: previewUrl,
        loading: true,
      };
    });

    setUploadedFiles((prevFiles) => {
      const updatedFiles = [...prevFiles, ...newFiles];
      newFiles.forEach((file, index) => {
        setTimeout(() => {
          setUploadedFiles((prev) =>
            prev.map((f, i) =>
              prevFiles.length + index === i ? { ...f, loading: false } : f
            )
          );
        }, 2000);
      });
      return updatedFiles;
    });
  };

  const handleRemoveFile = (id) => {
    const fileToRemove = uploadedFiles.find((f) => f.id === id);
    if (fileToRemove?.url) {
      URL.revokeObjectURL(fileToRemove.url);
    }
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleChange = useCallback(
    (e) => {
      if (!isSending) {
        debouncedTyping();
      }
      const message = e?.target?.innerText || e;
      setText(message);
    },
    [debouncedTyping, isSending]
  );
  const clearSendingMessage = useCallback(() => {
    setSendingMessage([]);
  }, []);

  const clearRecivingMessage = useCallback(() => {
    setRecivingMessage([]);
  }, []);

  const forwardMessage = useCallback(
    async (data, User) => {
      WebSocketService.disconnect();
      setPendingMessages(true);

      try {
        const token = getUserToken();

        WebSocketService.connect(token, User.chat_unique, User.is_group_chat);
        // websocketservice.disconnect();

        // if (pageFrom === "home ") {
        //   storeSelectedPerson(selectedUser);
        // } else {
        //   storeSelectedDms(selectedUser);
        // }

        // Prepare forward data
        const forwardData = {
          admin: adminId,
          company: [selectedComp.id],
          chat_unique_id: User.chat_unique,
          chat_name:
            User.is_group_chat === "0"
              ? "private"
              : User.is_group_chat === "1"
              ? "group"
              : "channel",
          register_id: userData.id,
          users_list: [userData.id, User.register_id],
          message: data.message,
          files: data.file_url || [],
          is_forward: "forward",
        };
        await new Promise((resolve) => setTimeout(resolve, 1000));

        WebSocketService.sendMessage(forwardData);
      } catch (error) {
        console.error("Error in forward:", error);
      } finally {
        setPendingMessages(false);
      }
    },
    [adminId, selectedComp.id, userData.id]
  );

  const pinMessage = useCallback(
    async (messageId, chatUniqueId) => {
      try {
        const pinData = {
          chat_unique_id: chatUniqueId,
          register_id: userData.id,
          message_id: messageId,
          is_pinned: "pinned",
        };
        setPinUpdates((prev) => {
          const filtered = prev.filter(
            (pin) =>
              !(
                pin.message_id === messageId &&
                pin.chat_unique_id === chatUniqueId
              )
          );
          return [...filtered, pinData];
        });
        WebSocketService.sendMessage(pinData);
      } catch (error) {
        console.error("Error pinning message:", error);
        toast.error("Unable to pin message. Please try again.", {
          position: "top-center",
          autoClose: 3000,
          closeButton: false,
          className: "custom_toast",
        });
      }
    },
    [userData.id]
  );

  const unpinMessage = useCallback(
    async (messageId, chatUniqueId) => {
      // setPinUpdates([]);

      try {
        const pinData = {
          chat_unique_id: chatUniqueId,
          register_id: userData.id,
          message_id: messageId,
          is_pinned: "unpin",
        };
        setPinUpdates((prev) => {
          const filtered = prev.filter(
            (pin) =>
              !(
                pin.message_id === messageId &&
                pin.chat_unique_id === chatUniqueId
              )
          );
          return [...filtered, pinData];
        });
        WebSocketService.sendMessage(pinData);
      } catch (error) {
        console.error("Error unpinning message:", error);
        toast.error("Unable to unpin message. Please try again.", {
          position: "top-center",
          autoClose: 3000,
          closeButton: false,
          className: "custom_toast",
        });
      }
    },
    [userData.id]
  );

  const clearPinUpdates = useCallback(() => {
    setPinUpdates([]);
  }, []);
  const deleteMessage = useCallback(
    async (messageId, chatUniqueId) => {
      setDeletedMessages((prev) => [...prev, { messageId, chatUniqueId }]);

      try {
        const deleteData = {
          chat_unique_id: chatUniqueId,
          register_id: userData.id,
          message_id: messageId,
          is_delete: "delete",
        };

        WebSocketService.sendMessage(deleteData);
      } catch (error) {
        console.error("Error in forward:", error);
      } finally {
      }
    },
    [userData.id]
  );
  const clearDeletedMessages = useCallback(() => {
    setDeletedMessages([]);
  }, []);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSend = async () => {
    setPendingMessages(true);
    setIsSending(true);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
    setUploadingFiles(uploadedFiles.map((file) => file.name));
    await delay(500);
    try {
      // const now = new Date();
      const filesData = await Promise.all(
        uploadedFiles.map(async (fileItem) => {
          return convertFileToBase64(fileItem.file);
        })
      );

      const cleanedMessage = replaceMentionsWithTags(text);
      const messageData = {
        admin: adminId,
        company: [selectedComp.id],

        // ...(!mentionInfo && {
        //   message: text ? JSON.stringify(text) : null,
        // }),
        chat_unique_id: selectedUser.chat_unique,
        files: filesData,

        chat_name:
          selectedUser.is_group_chat === "0"
            ? "private"
            : selectedUser.is_group_chat === "1"
            ? "group"
            : "channel",
        register_id: userData.id,
        users_list: [userData.id, selectedUser.register_id],
        message: text ? JSON.stringify(cleanedMessage) : [],

        // ...(mentionInfo && {
        //   is_mention: "mentioned",
        //   mention_user_id: menstion_id,
        //   message: JSON.stringify(cleanedMessage),
        // }),
        // ...(selectedUser.chat_unique
        //   ? {}
        //   : { users_list: [userData.id, selectedUser.register_id] }),
      };

      if (mentionInfo.length > 0) {
        messageData.is_mention = "mentioned";
        messageData.mention_user_id = menstion_id;
      }
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
      }
      //   setText('');
      setUploadedFiles([]);
      WebSocketService.sendMessage(messageData);

      // setText([]);
      setUploadedFiles([]);
      setMentionInfo([]);
    } catch (error) {
      setPendingMessages(false);

      toast.error("Unable to send message. Please try again.", {
        position: "top-center",
        autoClose: 3000,
        closeButton: false,
        className: "custom_toast",
      });
    } finally {
      // setIsSending(false);
      setIsSending(false);
      setUploadingFiles([]);
    }
  };

  useEffect(() => {
    return () => {
      uploadedFiles.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
    };
  }, [uploadedFiles]);

  return {
    text,
    setText,
    uploadedFiles,
    inputRef,
    handleFileChange,
    handleRemoveFile,
    handleSend,
    typing,
    handleChange,
    clearSendingMessage,
    reactions,
    clearRecivingMessage,
    insertMentionUser: setMentionInfo,
    sendingmessage,
    recivingMessage,
    pendingMessages,
    // isSending,
    setPendingMessages,
    uploadingFiles,
    setUploadedFiles,
    setTyping,
    forwardMessage,
    deleteMessage,
    deletedMessages,
    clearDeletedMessages,

    pinMessage,
    unpinMessage,
    pinUpdates,
    clearPinUpdates,
  };
};

export default useChatHome;
